import { useEffect, useMemo, useState } from "react";
import { CarFront, Check, ChevronDown, Pencil, Search, Star, X } from "lucide-react";
import api from "@/utils/api";

const control = "mt-1 h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-800 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:bg-slate-50 disabled:text-slate-400";
const pill = (active) => `rounded-full border px-3 py-1.5 text-xs font-semibold transition ${active ? "border-blue-600 bg-blue-50 text-blue-700" : "border-slate-200 bg-white text-slate-600 hover:border-blue-300"}`;

const fuelOf = (vehicle) => {
  const text = `${vehicle.fuel || ""} ${vehicle.type || ""} ${vehicle.modelBody || ""}`.toLowerCase();
  if (/electric|elektrik|battery/.test(text) && !/hybrid|hibrit/.test(text)) return "Elektrik";
  if (/hybrid|hibrit|e-hybrid|phev/.test(text)) return "Hibrit";
  if (/cng|lpg|doğal gaz|dogal gaz|bi[- ]?fuel/.test(text)) return "LPG / CNG";
  if (/tdi|sdi|diesel|dizel|\b[sd]?d\b/.test(text)) return "Dizel";
  if (/tsi|tfsi|fsi|benzin|petrol|gasoline/.test(text)) return "Benzinli";
  if (/\b(id\.?[3-7]|taycan|e-tron)\b/.test(text)) return "Elektrik";
  if (Number(vehicle.cc) > 0) return "Benzinli";
  return "Belirtilmemiş";
};

const unique = (items) => [...new Set(items.filter(Boolean))];

const typeLabel = (vehicle) => {
  let description = String(vehicle.type || "").trim();
  const body = String(vehicle.modelBody || "").trim();
  const escapedBody = body.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  if (escapedBody) description = description.replace(new RegExp(`^(?:VW|VOLKSWAGEN|AUDI|SEAT|SKODA|CUPRA|PORSCHE)\\s+${escapedBody}\\s*`, "i"), "");
  description = description.replace(/^(?:VW|VOLKSWAGEN|AUDI|SEAT|SKODA|CUPRA|PORSCHE)\s+/i, "").trim();
  const enginePeriod = vehicle.engineYearStart
    ? `Motor: ${vehicle.engineYearStart} - ${vehicle.engineYearEnd || "Güncel"}`
    : (vehicle.start || vehicle.end ? `${vehicle.start || "—"} - ${vehicle.end || "Güncel"}` : "");
  const details = [
    description || (vehicle.cc ? `${(Number(vehicle.cc) / 1000).toFixed(1)} l` : "Motor tipi"),
    vehicle.kw ? `${vehicle.kw} kW` : "",
    vehicle.ps ? `${vehicle.ps} PS` : "",
    enginePeriod,
    (vehicle.engineCodes || []).join(", "),
  ].filter(Boolean).join(" | ");
  return `${body}${body && details ? " · " : ""}${details}`;
};

export default function VehicleCatalogFilter({ value, onApply, onClear, onEditVehicle, drawAttention = false }) {
  const [open, setOpen] = useState(false);
  const [attentionActive, setAttentionActive] = useState(false);
  const [mode, setMode] = useState("guided");
  const [options, setOptions] = useState({ brands: [], modelsByBrand: {}, groups: {}, groupDetails: {} });
  const [brand, setBrand] = useState("");
  const [model, setModel] = useState("");
  const [year, setYear] = useState("");
  const [models, setModels] = useState([]);
  const [years, setYears] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [generations, setGenerations] = useState([]);
  const [cc, setCc] = useState("");
  const [ps, setPs] = useState("");
  const [variant, setVariant] = useState("");
  const [fuel, setFuel] = useState("");
  const [guidedEngine, setGuidedEngine] = useState("");
  const [design, setDesign] = useState("");
  const [typeNo, setTypeNo] = useState("");
  const [engineQuery, setEngineQuery] = useState("");
  const [engineCodes, setEngineCodes] = useState([]);
  const [engineCode, setEngineCode] = useState("");
  const [engineVehicles, setEngineVehicles] = useState([]);
  const [engineTypeNo, setEngineTypeNo] = useState("");
  const [category, setCategory] = useState("");
  const [subcategory, setSubcategory] = useState("");
  const [detail, setDetail] = useState("");

  useEffect(() => { api.get("/catalog/options").then((r) => setOptions(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    if (!drawAttention || value || open) {
      setAttentionActive(false);
      return;
    }
    const showTimer = setTimeout(() => setAttentionActive(true), 1200);
    const hideTimer = setTimeout(() => setAttentionActive(false), 4200);
    return () => { clearTimeout(showTimer); clearTimeout(hideTimer); };
  }, [drawAttention, value, open]);
  useEffect(() => {
    setModel(""); setYear(""); setModels([]); setYears([]); setVehicles([]); setGenerations([]);
    if (!brand) return;
    const bundledModels = options.modelsByBrand?.[brand];
    if (Array.isArray(bundledModels)) {
      setModels(bundledModels);
      return;
    }
    let active = true;
    api.get("/catalog/models", { params: { brand } }).then((r) => {
      if (active) setModels(r.data.items || []);
    });
    return () => { active = false; };
  }, [brand, options.modelsByBrand]);
  useEffect(() => {
    setYear(""); setYears([]); setVehicles([]); setGenerations([]);
    if (brand && model) api.get("/catalog/years", { params: { brand, model } }).then((r) => setYears(r.data.items || []));
  }, [brand, model]);
  useEffect(() => {
    setVehicles([]); setGenerations([]);
    if (brand && model) {
      const params = { brand, model, ...(year ? { year } : {}) };
      api.get("/catalog/vehicles", { params }).then((r) => setVehicles(r.data.items || []));
    }
  }, [brand, model, year]);
  useEffect(() => {
    setCc(""); setPs(""); setVariant(""); setFuel(""); setGuidedEngine(""); setDesign(""); setTypeNo("");
  }, [generations]);
  useEffect(() => {
    if (engineQuery.trim().length < 3) { setEngineCodes([]); return; }
    const timer = setTimeout(() => api.get("/catalog/engine-codes", { params: { q: engineQuery } }).then((r) => setEngineCodes(r.data.items || [])), 200);
    return () => clearTimeout(timer);
  }, [engineQuery]);
  useEffect(() => {
    setEngineVehicles([]);
    setEngineTypeNo("");
    if (!engineCode) return;
    api.get("/catalog/engine-vehicles", { params: { code: engineCode } })
      .then((r) => setEngineVehicles(r.data.items || []))
      .catch(() => setEngineVehicles([]));
  }, [engineCode]);
  useEffect(() => {
    if (!value) return;
    setAttentionActive(false);
    setCategory(value.category || "");
    setSubcategory(value.subcategory || "");
    setDetail(value.detail || "");
  }, [value]);

  const generationRows = useMemo(() => {
    const groups = new Map();
    vehicles.forEach((vehicle) => {
      const key = `${vehicle.modelBody}||${vehicle.generation}`;
      if (!groups.has(key)) groups.set(key, {
        key, title: vehicle.modelBody, generation: vehicle.generation, designs: new Set(),
        years: new Set(), engineCodes: new Set(), openEnded: false, typeNos: [],
      });
      const group = groups.get(key);
      group.typeNos.push(String(vehicle.typeNo));
      if (vehicle.design) group.designs.add(vehicle.design);
      (vehicle.years || []).forEach((item) => group.years.add(Number(item)));
      (vehicle.engineCodes || []).forEach((item) => group.engineCodes.add(item));
      if (!vehicle.end) group.openEnded = true;
    });
    return [...groups.values()].map((group) => {
      const groupYears = [...group.years].filter(Number.isFinite).sort((a, b) => a - b);
      const engines = [...group.engineCodes].sort((a, b) => a.localeCompare(b, "tr", { numeric: true }));
      return {
        ...group,
        design: [...group.designs].sort((a, b) => a.localeCompare(b, "tr")).join(" / "),
        startYear: groupYears[0] || "",
        endYear: group.openEnded ? "" : (groupYears.at(-1) || ""),
        engineLabel: engines.join(" - "),
      };
    });
  }, [vehicles]);

  const generationVehicles = useMemo(() => {
    const selected = new Set(generationRows.filter((row) => generations.includes(row.key)).flatMap((row) => row.typeNos));
    return vehicles.filter((vehicle) => selected.has(String(vehicle.typeNo)));
  }, [vehicles, generationRows, generations]);

  const detailOptions = useMemo(() => {
    const matches = (vehicle, omitted) => {
      if (omitted !== "cc" && cc && String(vehicle.cc) !== cc) return false;
      if (omitted !== "ps" && ps && String(vehicle.ps) !== ps) return false;
      if (omitted !== "variant" && variant && String(vehicle.typeNo) !== variant) return false;
      if (omitted !== "fuel" && fuel && fuelOf(vehicle) !== fuel) return false;
      if (omitted !== "engine" && guidedEngine && !(vehicle.engineCodes || []).includes(guidedEngine)) return false;
      if (omitted !== "design" && design && vehicle.design !== design) return false;
      if (omitted !== "typeNo" && typeNo && !String(vehicle.typeNo).includes(typeNo.trim())) return false;
      return true;
    };
    const rowsFor = (field) => generationVehicles.filter((vehicle) => matches(vehicle, field));
    const variantMap = new Map(rowsFor("variant").map((vehicle) => [String(vehicle.typeNo), vehicle]));
    return {
      cc: unique(rowsFor("cc").map((v) => v.cc && String(v.cc))).sort((a, b) => Number(a) - Number(b)),
      ps: unique(rowsFor("ps").map((v) => v.ps && String(v.ps))).sort((a, b) => Number(a) - Number(b)),
      variants: [...variantMap.values()].sort((a, b) => typeLabel(a).localeCompare(typeLabel(b), "tr")),
      fuel: unique(rowsFor("fuel").map(fuelOf)).sort((a, b) => a.localeCompare(b, "tr")),
      engine: unique(rowsFor("engine").flatMap((v) => v.engineCodes || [])).sort(),
      design: unique(rowsFor("design").map((v) => v.design)).sort((a, b) => a.localeCompare(b, "tr")),
      typeNo: unique(rowsFor("typeNo").map((v) => String(v.typeNo))).sort((a, b) => Number(a) - Number(b)),
    };
  }, [generationVehicles, cc, ps, variant, fuel, guidedEngine, design, typeNo]);

  useEffect(() => {
    if (variant && !detailOptions.variants.some((item) => String(item.typeNo) === variant)) setVariant("");
  }, [detailOptions.variants, variant]);

  const selectedVehicles = useMemo(() => generationVehicles.filter((vehicle) => {
    if (cc && String(vehicle.cc) !== cc) return false;
    if (ps && String(vehicle.ps) !== ps) return false;
    if (variant && String(vehicle.typeNo) !== variant) return false;
    if (fuel && fuelOf(vehicle) !== fuel) return false;
    if (guidedEngine && !(vehicle.engineCodes || []).includes(guidedEngine)) return false;
    if (design && vehicle.design !== design) return false;
    if (typeNo && !String(vehicle.typeNo).includes(typeNo.trim())) return false;
    return true;
  }), [generationVehicles, cc, ps, variant, fuel, guidedEngine, design, typeNo]);

  const selectedTypeNos = selectedVehicles.map((vehicle) => String(vehicle.typeNo));
  const appliedTypeNos = selectedTypeNos.length ? selectedTypeNos : (value?.typeNos || []).map(String);
  const appliedEngineCode = mode === "engine" ? (engineCode || value?.engineCode || "") : "";
  const editableVehicles = mode === "engine"
    ? (engineTypeNo ? engineVehicles.filter((vehicle) => String(vehicle.typeNo) === engineTypeNo) : (engineVehicles.length === 1 ? engineVehicles : []))
    : selectedVehicles;
  const subgroups = options.groups?.[category] || [];
  const details = options.groupDetails?.[category]?.[subcategory] || [];
  const vehicleReady = mode === "engine" ? Boolean(appliedEngineCode) : appliedTypeNos.length > 0;

  const apply = (nextCategory = category, nextSubcategory = subcategory, nextDetail = detail) => {
    const nextSubgroups = options.groups?.[nextCategory] || [];
    if (!vehicleReady || !nextCategory || (nextSubgroups.length && !nextSubcategory)) return;
    const hasActivePicker = mode === "engine" ? Boolean(engineCode) : selectedTypeNos.length > 0;
    const vehicleLabel = !hasActivePicker && value?.label ? value.label : mode === "engine"
      ? `Motor kodu: ${engineCode}${engineTypeNo ? ` · ${typeLabel(engineVehicles.find((item) => String(item.typeNo) === engineTypeNo) || {})}` : ""}`
      : `${brand} · ${model}${year ? ` · ${year}` : ""} · ${generations.length} kasa`;
    onApply({
      typeNos: mode === "guided" ? appliedTypeNos : (engineTypeNo ? [engineTypeNo] : (value?.typeNos || [])),
      engineCode: mode === "engine" ? appliedEngineCode : "",
      category: nextCategory,
      subcategory: nextSubcategory,
      detail: nextDetail,
      label: vehicleLabel,
    });
    setOpen(false);
  };

  const chooseCategory = (item) => {
    setCategory(item);
    setSubcategory("");
    setDetail("");
    if (!(options.groups?.[item] || []).length) apply(item, "");
  };
  const chooseSubcategory = (item) => {
    setSubcategory(item);
    setDetail("");
    apply(category, item, "");
  };
  const chooseDetail = (item) => {
    setDetail(item);
    apply(category, subcategory, item);
  };

  const groupPicker = (compact = false) => <>
    <div className={compact ? "flex flex-wrap items-center gap-2" : "mt-4 border-t border-slate-100 pt-3"}>
      <div className={`${compact ? "w-20 shrink-0" : "mb-2"} text-xs font-bold text-slate-600`}>Ürün grubu</div>
      <div className="flex flex-wrap gap-2">{Object.keys(options.groups || {}).map((item) => <button type="button" key={item} onClick={() => chooseCategory(item)} className={pill(category === item)}>{item}</button>)}</div>
    </div>
    {category && subgroups.length > 0 && <div className={`${compact ? "mt-2 flex flex-wrap items-center gap-2" : "mt-3"}`}>
      <div className={`${compact ? "w-20 shrink-0" : "mb-2"} text-xs font-bold text-slate-600`}>Alt grup</div>
      <div className="flex flex-wrap gap-2">{subgroups.map((item) => <button type="button" key={item} onClick={() => chooseSubcategory(item)} className={pill(subcategory === item)}>{item}</button>)}</div>
    </div>}
    {category && subcategory && details.length > 0 && <div className={`${compact ? "mt-2 flex flex-wrap items-center gap-2" : "mt-3"}`}>
      <div className={`${compact ? "w-20 shrink-0" : "mb-2"} text-xs font-bold text-slate-600`}>Seçenek <span className="font-normal text-slate-400">(isteğe bağlı)</span></div>
      <div className="flex flex-wrap gap-2">{details.map((item) => <button type="button" key={item} onClick={() => chooseDetail(item)} className={pill(detail === item)}>{item}</button>)}</div>
    </div>}
  </>;

  return (
    <section className={`mx-5 mt-2 ${value && !open ? "sm:sticky sm:top-14 z-30" : ""}`}>
      <div className={`rounded-xl ${value && !open ? "border border-slate-200 bg-white p-3 shadow-sm" : ""}`}>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => { setAttentionActive(false); setOpen((current) => !current); }}
            className={`inline-flex h-10 items-center gap-2 rounded-lg bg-[#0D1B3E] px-4 text-sm font-bold text-white shadow-sm ${attentionActive ? "vehicle-picker-attention" : ""}`}
          >
            <CarFront size={17}/> Araç Seç <ChevronDown size={15} className={open ? "rotate-180" : ""}/>
          </button>
          {attentionActive && <span className="vehicle-picker-hint">Aracınıza göre doğru parçayı bulun</span>}
          {value && <div className="inline-flex min-w-0 items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs font-semibold text-blue-900">
            <span className="max-w-[720px] truncate">{value.label}</span>
            {value.primaryOem && <span className="inline-flex shrink-0 items-center gap-1 rounded bg-white px-2 py-0.5 font-mono text-blue-700">OEM: {value.primaryOem}{value.primaryOemVerified && <Star size={13} fill="currentColor" className="text-amber-500" aria-label="İnsan tarafından doğrulandı" title={`${value.primaryOemVerificationRole === "satis_destek" ? "Satış destek" : "Admin"} tarafından doğrulandı${value.primaryOemVerifiedBy ? ` · ${value.primaryOemVerifiedBy}` : ""}`}/>}</span>}
            <button type="button" aria-label="Araç filtresini temizle" onClick={onClear}><X size={14}/></button>
          </div>}
        </div>
        {value && !open && <div className="mt-2 border-t border-slate-100 pt-2">{groupPicker(true)}</div>}
      </div>

      {open && <div className="mt-2 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
          <div><div className="text-[11px] font-bold uppercase tracking-[.16em] text-blue-600">Araç seçimi</div><h2 className="text-base font-bold text-[#0D1B3E]">Araç bilgileri</h2></div>
          <button type="button" onClick={() => setOpen(false)} className="rounded-full p-2 text-slate-400 hover:bg-slate-100"><X size={18}/></button>
        </div>
        <div className="flex gap-5 border-b border-slate-100 px-4 pt-2">
          {[["guided","Adım adım seç"],["engine","Motor koduyla bul"]].map(([key, label]) => <button key={key} onClick={() => setMode(key)} className={`border-b-2 px-1 py-2 text-sm font-semibold ${mode === key ? "border-blue-600 text-blue-700" : "border-transparent text-slate-500"}`}>{label}</button>)}
        </div>
        <div className="p-4">
          {mode === "guided" ? <>
            <div className="grid gap-3 md:grid-cols-3">
              <label className="text-xs font-bold text-slate-600">1. Üretici<select className={control} value={brand} onChange={(e) => setBrand(e.target.value)}><option value="">Üretici seçin</option>{options.brands.map((item) => <option key={item}>{item}</option>)}</select></label>
              <label className="text-xs font-bold text-slate-600">2. Model {brand && !model && <span className="vehicle-next-step-badge">Sıradaki</span>}<select className={`${control} ${brand && !model ? "vehicle-next-step-control" : ""}`} value={model} disabled={!brand} onChange={(e) => setModel(e.target.value)}><option value="">Model seçin</option>{models.map((item) => <option key={item}>{item}</option>)}</select></label>
              <label className="text-xs font-bold text-slate-600">3. Model yılı <span className="font-normal text-slate-400">(isteğe bağlı)</span> {model && !year && <span className="vehicle-next-step-badge">Sıradaki</span>}<select className={`${control} ${model && !year ? "vehicle-next-step-control" : ""}`} value={year} disabled={!model} onChange={(e) => setYear(e.target.value)}><option value="">Tüm yıllar</option>{years.map((item) => <option key={item}>{item}</option>)}</select></label>
            </div>
            {generationRows.length > 0 && <div className={`mt-3 rounded-xl ${year && generations.length === 0 ? "vehicle-next-step-panel" : ""}`}><div className="mb-2 text-xs font-bold text-slate-600">4. Kasa / Nesil <span className="ml-1 font-normal text-slate-400">Birden fazla seçebilirsiniz</span> {year && generations.length === 0 && <span className="vehicle-next-step-badge">Sıradaki</span>}</div><div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">{generationRows.map((row) => {
              const active = generations.includes(row.key); return <button type="button" key={row.key} onClick={() => setGenerations((current) => active ? current.filter((item) => item !== row.key) : [...current, row.key])} className={`flex min-h-16 min-w-0 items-start justify-between gap-2 rounded-lg border p-3 text-left ${active ? "border-blue-600 bg-blue-50 ring-1 ring-blue-600" : "border-slate-200 hover:border-slate-300"}`}><span className="min-w-0"><strong className="block text-xs text-slate-900">{row.title}</strong><small className="mt-1 block text-slate-500">{row.generation || "Kasa kodu yok"} - {row.design || "TecDoc araç tipi"} - {row.startYear || "?"}&gt;&gt;{row.endYear}</small>{row.engineLabel && <small className="mt-1 block truncate text-[10px] text-slate-400" title={row.engineLabel}>{row.engineLabel}</small>}</span>{active && <Check size={15} className="shrink-0 text-blue-600"/>}</button>;
            })}</div></div>}
            {generationVehicles.length > 0 && <div className="mt-3 grid gap-2 md:grid-cols-12">
              <label className="text-xs font-bold text-slate-600 md:col-span-3 xl:col-span-2">Motor hacmi (cc)<select className={control} value={cc} onChange={(e) => { setCc(e.target.value); setVariant(""); }}><option value="">Tümü</option>{detailOptions.cc.map((item) => <option key={item}>{item}</option>)}</select></label>
              <label className="text-xs font-bold text-slate-600 md:col-span-3 xl:col-span-2">Güç (PS)<select className={control} value={ps} onChange={(e) => { setPs(e.target.value); setVariant(""); }}><option value="">Tümü</option>{detailOptions.ps.map((item) => <option key={item}>{item}</option>)}</select></label>
              <label className="text-xs font-bold text-slate-600 md:col-span-6 xl:col-span-8">Tip {generations.length > 0 && !variant && <span className="vehicle-next-step-badge">Sıradaki</span>}<select className={`${control} ${generations.length > 0 && !variant ? "vehicle-next-step-control" : ""}`} value={variant} onChange={(e) => setVariant(e.target.value)}><option value="">Tüm tipler ({detailOptions.variants.length})</option>{detailOptions.variants.map((item) => <option key={item.typeNo} value={String(item.typeNo)}>{typeLabel(item)}</option>)}</select></label>
              <label className="text-xs font-bold text-slate-600 md:col-span-3">Yakıt<select className={control} value={fuel} onChange={(e) => { setFuel(e.target.value); setVariant(""); }}><option value="">Tümü</option>{detailOptions.fuel.map((item) => <option key={item}>{item}</option>)}</select></label>
              <label className="text-xs font-bold text-slate-600 md:col-span-3">Motor kodu<select className={control} value={guidedEngine} onChange={(e) => { setGuidedEngine(e.target.value); setVariant(""); }}><option value="">Tümü</option>{detailOptions.engine.map((item) => <option key={item}>{item}</option>)}</select></label>
              <label className="text-xs font-bold text-slate-600 md:col-span-3">Yapı tarzı<select className={control} value={design} onChange={(e) => { setDesign(e.target.value); setVariant(""); }}><option value="">Tümü</option>{detailOptions.design.map((item) => <option key={item}>{item}</option>)}</select></label>
              <label className="text-xs font-bold text-slate-600 md:col-span-3">TecDoc Tip No.<input className={control} value={typeNo} onChange={(e) => { setTypeNo(e.target.value.replace(/\D/g, "")); setVariant(""); }} placeholder="Tip No."/></label>
            </div>}
          </> : <div className="grid gap-4 lg:grid-cols-2">
            <div><label className="text-xs font-bold text-slate-600">Motor kodu veya kod ailesi<div className="relative mt-1"><Search size={15} className="absolute left-3 top-3 text-slate-400"/><input className={`${control} mt-0 pl-9`} value={engineQuery} onChange={(e) => { setEngineQuery(e.target.value.toUpperCase()); setEngineCode(""); }} placeholder="Örn. CAY, CLH, BLS"/></div></label>{engineCodes.length > 0 && <div className="mt-1 max-h-40 overflow-auto rounded-lg border border-slate-200 bg-white p-1">{engineCodes.map((code) => <button type="button" key={code} onClick={() => { setEngineCode(code); setEngineQuery(code); setEngineCodes([]); }} className={`block w-full rounded px-3 py-2 text-left text-sm hover:bg-slate-50 ${engineCode === code ? "font-bold text-blue-700" : ""}`}>{code}</button>)}</div>}</div>
            {engineCode && <label className="text-xs font-bold text-slate-600">Tip <span className="font-normal text-slate-400">(isteğe bağlı)</span><select className={control} value={engineTypeNo} onChange={(e) => setEngineTypeNo(e.target.value)}><option value="">Tüm tipler ({engineVehicles.length})</option>{engineVehicles.map((vehicle) => <option key={vehicle.typeNo} value={String(vehicle.typeNo)}>{typeLabel(vehicle)}</option>)}</select></label>}
          </div>}
          {onEditVehicle && ((mode === "guided" && generationVehicles.length > 0) || (mode === "engine" && engineVehicles.length > 0)) && <div className="mt-4 rounded-lg border border-blue-100 bg-blue-50 p-3">
            <div className="mb-2 text-xs font-bold text-blue-900">Mevcut araç tiplerini düzenle</div>
            {editableVehicles.length > 0 ? <div className="grid max-h-56 gap-2 overflow-y-auto md:grid-cols-2 xl:grid-cols-3">{editableVehicles.map((vehicle) => <button type="button" key={vehicle.typeNo} onClick={() => onEditVehicle(vehicle)} className="flex items-center justify-between gap-3 rounded-lg border border-blue-200 bg-white px-3 py-2 text-left text-xs text-blue-900 hover:border-blue-500"><span className="min-w-0"><b className="block truncate">{vehicle.modelBody} · Tip {vehicle.typeNo}</b><span className="mt-0.5 block truncate text-[11px] text-slate-500">{vehicle.cc || "—"} cc · {vehicle.ps || "—"} PS · {(vehicle.engineCodes || []).join(", ") || "Motor kodu yok"}</span></span><span className="shrink-0 font-bold text-blue-700"><Pencil size={14} className="mr-1 inline"/>Düzenle</span></button>)}</div> : <div className="text-xs text-blue-800">Motor koduyla aramada düzenlemek için Tip alanından tek araç tipi seçin.</div>}
          </div>}
          {groupPicker()}
        </div>
        <div className="border-t border-slate-100 bg-slate-50 px-4 py-3 text-xs text-slate-500">{vehicleReady ? "Ürün grubu ve alt gruba tıkladığınızda uyumlu parçalar otomatik açılır." : "Önce araç veya motor kodunu seçin; ardından ürün grubunu sonuna kadar seçin."}</div>
      </div>}
    </section>
  );
}
